/**
 * "Company country": where the customer is based, as an ISO 3166-1 alpha-2 code. Only Avrio (a Super Admin)
 * can change it; a customer's own Admin sees it read-only.
 *
 * It sets the default language of the company's candidate summaries. Only English exists today, so it has
 * no visible effect yet, and the section says so rather than implying otherwise.
 *
 * PUT /api/customers/{id}/country { countryCode } (avrio-backend#262). A blank value clears it; absent
 * means unknown, and unknown means English. The backend decides what is a valid code: this field only
 * shows the country name it recognises, to catch a typo before saving.
 */
import { memo, useEffect, useState } from 'react';
import { Alert, Button, Input, Space, Tag, Typography } from 'antd';
import axios from '../../../../axiosConfig';
import { CustomerConfig } from '../../types';
import styles from './index.module.css';

const { Title, Text } = Typography;

/** The English name of a region code, or undefined when the browser does not recognise it. */
export const countryName = (code: string): string | undefined => {
  if (!/^[A-Za-z]{2}$/.test(code)) return undefined;
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'region' }).of(
      code.toUpperCase(),
    );
    return name && name !== code.toUpperCase() && name !== 'Unknown Region'
      ? name
      : undefined;
  } catch {
    return undefined;
  }
};

interface Props {
  customerId: number;
  customerName: string;
  countryCode?: string;
  /** Super Admin only. Everyone else sees the country and cannot change it. */
  canEdit: boolean;
  /** Called with the customer as the backend now has it. */
  onChanged: (updated: CustomerConfig) => void;
}

const CustomerCountrySection = ({
  customerId,
  customerName,
  countryCode,
  canEdit,
  onChanged,
}: Props) => {
  const [draft, setDraft] = useState(countryCode ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A different customer, or a saved change, replaces the draft.
  useEffect(() => {
    setDraft(countryCode ?? '');
    setError(null);
  }, [countryCode, customerId]);

  const trimmed = draft.trim();
  const name = trimmed ? countryName(trimmed) : undefined;
  const changed = trimmed.toUpperCase() !== (countryCode ?? '');
  // Blank clears; otherwise two letters the browser recognises as a country.
  const valid = trimmed === '' || name !== undefined;

  const save = () => {
    setSaving(true);
    setError(null);
    axios
      .put<CustomerConfig>(`/api/customers/${customerId}/country`, {
        countryCode: trimmed.toUpperCase(),
      })
      .then((res) => onChanged(res.data))
      .catch((err) => {
        const message = err?.response?.data?.message;
        setError(
          typeof message === 'string' && message.trim()
            ? `Could not save: ${message}`
            : 'Could not change the country. Try again.',
        );
      })
      .finally(() => setSaving(false));
  };

  const current = countryCode ? countryName(countryCode) : undefined;

  return (
    <section
      className={styles.section}
      aria-labelledby="customer-country-title"
    >
      <Title level={5} id="customer-country-title" className={styles.title}>
        Company country
      </Title>

      <span className={styles.body}>
        {countryCode ? <Tag>{countryCode}</Tag> : <Tag>Not set</Tag>}
        <Text>
          {countryCode
            ? `${customerName} is based in ${current ?? countryCode}.`
            : `${customerName} has no country set.`}
        </Text>
      </span>
      <Text type="secondary" className={styles.note}>
        The country sets the default language of the company’s candidate
        summaries. Only English is available today, so this has no visible
        effect yet. Only Avrio can change it.
      </Text>

      {canEdit && (
        <>
          <Space align="start">
            <div>
              <label htmlFor="customer-country-input" className={styles.label}>
                Country code
              </label>
              <Input
                id="customer-country-input"
                className={styles.input}
                maxLength={2}
                value={draft}
                placeholder="e.g. NL"
                onChange={(event) => setDraft(event.target.value.toUpperCase())}
                status={valid ? undefined : 'error'}
              />
            </div>
            <Button
              type="primary"
              className={styles.save}
              onClick={save}
              loading={saving}
              disabled={!changed || !valid}
            >
              Save country
            </Button>
          </Space>
          <Text
            type={valid ? 'secondary' : 'danger'}
            className={styles.hint}
            role="status"
          >
            {trimmed === ''
              ? 'Leave empty to clear the country.'
              : name
                ? name
                : 'Not a recognised country code.'}
          </Text>
        </>
      )}
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          className={styles.notice}
        />
      )}
    </section>
  );
};

export default memo(CustomerCountrySection);
